import { defineStore } from "pinia";
import { computed, ref } from "vue";

import {
  type DataSource,
  normalizeDataSources,
  type RawCampaignLink,
  type RawCrmConfig,
} from "@/api/admin/dataSources";
import ingestionApi from "@/api/admin/ingestion";
import { useAdminStore } from "@/stores/adminStore";
import { useIngestionStore } from "@/stores/ingestionStore";

export const useDataSourcesStore = defineStore("dataSources", () => {
  const dataSources = ref<DataSource[]>([]);
  const isLoading = ref(false);
  const error = ref<null | string>(null);

  const getById = computed(
    () => (id: string) => dataSources.value.find((d) => d.crmConfigId === id) ?? null
  );

  // Bumped by $reset: a load in flight at sign-out holds the previous org's sources.
  let generation = 0;

  async function loadDataSources() {
    const requestGeneration = generation;
    const adminStore = useAdminStore();
    const ingestionStore = useIngestionStore();
    isLoading.value = true;
    error.value = null;
    const errors: string[] = [];

    // Settle the two source lists (+ campaigns) independently so one failing still
    // shows the other (spec: non-blocking partial failure, don't blank the page).
    // fetchAdminCampaigns returns void; read getAllCampaigns after it settles.
    const [crmRes, ingRes] = await Promise.allSettled([
      adminStore.fetchCrmConfigs(true) as Promise<RawCrmConfig[]>,
      ingestionApi.listSources(),
      adminStore.fetchAdminCampaigns(),
    ]);

    const crmConfigs = crmRes.status === "fulfilled" ? (crmRes.value ?? []) : [];
    if (crmRes.status === "rejected") {
      errors.push(
        crmRes.reason instanceof Error ? crmRes.reason.message : "Failed to load CRM connections"
      );
    }
    const ingestionSources = ingRes.status === "fulfilled" ? (ingRes.value ?? []) : [];
    if (ingRes.status === "rejected") {
      errors.push(
        ingRes.reason instanceof Error ? ingRes.reason.message : "Failed to load ingestion sources"
      );
    }

    if (requestGeneration !== generation) return;

    // Review counts come from the ingestion summary (best-effort, non-blocking).
    if (!ingestionStore.summary) {
      try {
        await ingestionStore.loadSummary();
      } catch {
        /* non-blocking — review counts just default to 0 */
      }
    }

    if (requestGeneration !== generation) return;
    const campaigns = adminStore.getAllCampaigns as RawCampaignLink[];
    dataSources.value = normalizeDataSources(
      crmConfigs,
      ingestionSources,
      ingestionStore.summary?.reviewByCampaign ?? [],
      campaigns ?? []
    );
    error.value = errors.length ? errors.join("; ") : null;
    isLoading.value = false;
  }

  /** A setup store has no working Pinia `$reset`; sign-out (`clearAllStores`) needs this one. */
  function $reset() {
    generation++;
    dataSources.value = [];
    isLoading.value = false;
    error.value = null;
  }

  return { $reset, dataSources, error, getById, isLoading, loadDataSources };
});
